import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/auth/AuthContext";
import constants from "../../Utils/constants";
import Loader from "../../components/Loader";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import TenantContext from "../../context/tenant/TenantContext";
import useTranslator from "../../hooks/useTranslator";
import { haveValue, toast } from "../../helper/common";
import AuthenticateYourId from "../onboarding/authenticateYourId";
import BasicInfoModal from "../onboarding/BasicInfoModal";
import RequiredDocumentsModal from "../onboarding/RequiredDocumentsModal";
import LandLordContext from "../../context/landlord/LandLordContext";
import TenantAdditionalChecks from "./TenantAdditionalChecks";
import TenantChecksUploadDocument from "./TenantChecksUploadDocument";
import TenantChecksIncomeModel from "./TenantChecksIncomeModel";
import { STEPS, getVisibleSteps } from "./tenantChecksConfig";
import TenantChecksSidebar from "./TenantChecksSidebar";
import TenantChecksGroupStep from "./TenantChecksGroupStep";
import TenantChecksCompletedModal from "./TenantChecksCompletedModal";
import {
  getTenantCheckUpdatePayload,
  getTenantIdFromResponse,
} from "./tenantChecksUtils";

function TenantChecksOnboarding() {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const authContext = useContext(AuthContext);
  const { user: originalUser } = authContext;
  const { submitProtectedHousingDocuments } = useContext(TenantContext);
  const {
    createTenant,
    createLLGuarantor,
    fetchingCreatedTenant,
    updateLLTenant,
    updateAdditionalInfo,
  } = useContext(LandLordContext);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stepId, setStepId] = useState("checks");
  const [formData, setFormData] = useState({
    currency: "EUR",
    employment_status: "",
    employment_category: "",
  });
  const [guarantorFormData, setGuarantorFormData] = useState({
    currency: "EUR",
    employment_status: "",
    employment_category: "",
  });
  const [pageLoading, setPageLoading] = useState(false);
  const [user, setUser] = useState({});
  const [additionalInfo, setAdditionalInfo] = useState([]);
  const [showPassedModal, setShowPassedModal] = useState(false);
  const tenant_id = searchParams.get("tenant_id");
  const reCheck = searchParams.get("reCheck");
  const [runDetectKycDocs, setRunDetectKycDocs] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [guarantorPhoneNumber, setGuarantorPhoneNumber] = useState("");
  const [selectedProtectedHousingDocs, setSelectedProtectedHousingDocs] =
    useState([]);
  const [addedFiles, setAddedFiles] = useState([]);
  const [selectedOnboardingUsers, setSelectedOnboardingUsers] = useState([]);

  const visibleSteps = useMemo(
    () => getVisibleSteps(additionalInfo),
    [additionalInfo]
  );

  useEffect(() => {
    if (!visibleSteps.find((s) => s.id === stepId)) {
      const fallback = visibleSteps[visibleSteps.length - 1]?.id || "checks";
      setStepId(fallback);
    }
  }, [stepId, visibleSteps]);

  const applyProfileData = (
    _user,
    _setFormData,
    _setPhoneNumber,
    llGuarantor
  ) => {
    const isEmptyUser = !haveValue(_user) || Object.keys(_user).length === 0;
    if (isEmptyUser) {
      _setFormData((prev) => ({
        fname: "",
        lname: "",
        identity_number: "",
        post_code: "",
        dob: "",
        employment_status: "",
        employment_category: "",
        current_annual_income: "",
        protected_housing: false,
        email: "",
        gurantor_relation: "",
        phone_number: "",
        currency: prev?.currency || "EUR",
      }));
      _setPhoneNumber("");
      return;
    }
    _setFormData({
      fname: _user?.fname || "",
      lname: _user?.lname || "",
      identity_number: _user?.dni_nie || _user?.passport_id || "",
      post_code: _user?.post_code || "",
      dob: _user?.dob ? _user.dob.split("T")[0] : "",
      employment_status: _user?.employment_status || "",
      employment_category: _user?.employment_category || "",
      current_annual_income: _user?.current_annual_income ?? "",
      protected_housing: _user?.protected_housing || false,
      email: _user?.email || "",
      gurantor_relation: _user?.gurantor_relation || "",
      phone_number: _user?.phone_number || "",
      currency: _user?.currency || "EUR",
    });
    _setPhoneNumber(_user?.phone_number || "");
    if (!llGuarantor) {
      setSelectedProtectedHousingDocs(
        !_user?.protected_housing
          ? ["no"]
          : _user?.tenant_extra_field_id?.public_housing_docs
      );
      const savedChecks =
        _user?.tenant_extra_field_id?.tenant_checks?.additional_info ?? [];
      setAdditionalInfo((prev) =>
        savedChecks.length ? savedChecks : prev.length ? prev : savedChecks
      );
      if (haveValue(reCheck) && reCheck) {
        setStepId("financial");
      } else if (_user?.step) {
        const legacyMap = {
          1: "checks",
          2: "basic",
          3: "identity",
          4: "guarantor",
          5: "documents",
          6: "financial",
        };
        const mapped =
          typeof _user.step === "string" ? _user.step : legacyMap[_user.step];
        if (mapped) setStepId(mapped);
      }
    }
  };

  const loadData = async (needLoading = true, customTenantId) => {
    try {
      if (needLoading) setPageLoading(true);
      let userData;
      const tid = customTenantId ?? searchParams.get("tenant_id") ?? tenant_id;
      if (tid) userData = await fetchingCreatedTenant(tid);
      const u = userData?.data ?? {};
      if (tid) setUser(u);
      // don't wipe user on empty fetch
      if (u?._id) {
        applyProfileData(u, setFormData, setPhoneNumber);
        const guarantorData = u?.gurantor_id?.[0] ?? {};
        if (guarantorData)
          guarantorData.gurantor_relation =
            u?.gurantor_added_id?.[0]?.gurantor_relation;
        applyProfileData(
          haveValue(guarantorData) ? guarantorData : {},
          setGuarantorFormData,
          setGuarantorPhoneNumber,
          true
        );
      }
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [location.pathname]);

  const getNextId = (cur = stepId) => {
    const idx = visibleSteps.findIndex((s) => s.id === cur);
    return visibleSteps[idx + 1]?.id || null;
  };
  const getPrevId = (cur = stepId) => {
    const idx = visibleSteps.findIndex((s) => s.id === cur);
    return visibleSteps[idx - 1]?.id || null;
  };

  const STEP_ID_TO_NUMBER = {
    checks: 1,
    basic: 2,
    identity: 3,
    guarantor: 4,
    documents: 5,
    financial: 6,
  };

  const createUserProfile = async (data, userType) => {
    try {
      if (!haveValue(data?.is_edit)) data.is_edit = true;
      data.role = userType === "llGuarantor" ? "llGuarantor" : "llTenant";
      if (userType !== "llGuarantor") {
        data.step = 2;
      }
      let llTTResponse;
      if (userType === "llGuarantor" && !user?.gurantor_id?.length) {
        llTTResponse = await createLLGuarantor({
          ...data,
          tenant_id: user?._id || tenant_id,
        });
      } else if (user?._id) {
        const user_id =
          userType === "llGuarantor" ? user?.gurantor_id?.[0]?._id : user?._id;
        llTTResponse = await updateLLTenant({ ...data, id: user_id });
      } else {
        try {
          llTTResponse = await createTenant(data);
        } catch (error) {
          toast.error(
            error.response?.data?.errormsg ||
              error.response?.data?.error ||
              error.response?.data?.message ||
              _t("somethingWentWrong")
          );
          throw error;
        }
      }
      const newTenantId = getTenantIdFromResponse(llTTResponse);
      const resolvedTenantId = newTenantId || user?._id;

      if (userType !== "llGuarantor" && !resolvedTenantId) {
        toast.error(_t("somethingWentWrong"));
        throw new Error("Tenant creation response did not include an ID");
      }

      if (userType !== "llGuarantor") {
        await submitProtectedHousingDocuments({
          allDocuments: selectedProtectedHousingDocs ?? [],
          llCreated: true,
          user_id: resolvedTenantId,
        });
      }
      if (userType !== "llGuarantor" && !user?._id) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("tenant_id", newTenantId);
            return next;
          },
          { replace: true }
        );
        await updateAdditionalInfo({
          id: newTenantId,
          additional_info: additionalInfo,
        });
      }
      await loadData(
        false,
        userType === "llGuarantor" ? user?._id || tenant_id : newTenantId
      );
      return resolvedTenantId;
    } catch (err) {
      throw err;
    }
  };

  const updateUserProfile = async (data) => {
    try {
      const tid =
        data.id || searchParams.get("tenant_id") || tenant_id || user?._id;
      if (!tid) throw new Error("Missing tenant id for update");
      const payload = getTenantCheckUpdatePayload(data, tid);
      const r = await updateLLTenant(payload);
      await loadData(false, tid);
      return r;
    } catch (e) {
      console.log(e);
      throw e;
    }
  };

  const requiresSubcategory = (status) =>
    [
      constants.public_servant,
      constants.employed,
      constants.self_employed,
    ].includes(status);

  const completeTenantCheck = async () => {
    const id = user?._id || tenant_id;
    if (!id) return;
    await updateLLTenant({ profile_completed: true, id });
    setShowPassedModal(true);
  };

  const formHandler = {
    next: async (opts = {}) => {
      const n = getNextId();
      if (!n) {
        await completeTenantCheck();
        return;
      }
      // Persist step before navigating; use explicit id if caller has it (avoids stale closure)
      const tid =
        opts.id || searchParams.get("tenant_id") || tenant_id || user?._id;
      if (!tid) {
        // tenant not yet created — stay on basic
        setStepId("basic");
        return;
      }
      await updateUserProfile({
        id: tid,
        step: STEP_ID_TO_NUMBER[n] ?? n,
      });
      setStepId(n);
    },
    prev: () => {
      const p = getPrevId();
      if (p) setStepId(p);
    },
  };

  const guarantor = user?.gurantor_id?.[0];

  const renderStep = () => {
    const cfg = STEPS.find((s) => s.id === stepId) || STEPS[0];
    if (cfg.id === "checks")
      return (
        <TenantAdditionalChecks
          additionalInfo={additionalInfo}
          setAdditionalInfo={setAdditionalInfo}
          user={user}
          step={stepId}
          setStep={setStepId}
          formHandler={formHandler}
          updateUserProfile={updateUserProfile}
          getNextStep={getNextId}
        />
      );
    if (cfg.id === "basic")
      return (
        <BasicInfoModal
          user={user}
          formData={formData}
          setFormData={setFormData}
          createUserProfile={createUserProfile}
          formHandler={formHandler}
          requiresSubcategory={requiresSubcategory}
          originalUser={originalUser}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          selectedProtectedHousingDocs={selectedProtectedHousingDocs}
          setSelectedProtectedHousingDocs={setSelectedProtectedHousingDocs}
          usedFor="tenantChecks"
        />
      );
    if (cfg.id === "documents")
      return (
        <TenantChecksUploadDocument
          user={user}
          formHandler={formHandler}
          updateUserProfile={updateUserProfile}
          step={stepId}
          getNextStep={getNextId}
          onComplete={completeTenantCheck}
          selectedOnboardingUsers={selectedOnboardingUsers}
          setSelectedOnboardingUsers={setSelectedOnboardingUsers}
        />
      );
    if (cfg.id === "identity")
      return (
        <AuthenticateYourId
          formHandler={formHandler}
          user={user}
          updateUserProfile={updateUserProfile}
          setRunDetectKycDocs={setRunDetectKycDocs}
          addedFiles={addedFiles}
          setAddedFiles={setAddedFiles}
          usedFor="tenantChecks"
          step={stepId}
          getNextStep={getNextId}
          isTenantCheck={true}
        />
      );
    if (cfg.id === "financial")
      return (
        <TenantChecksIncomeModel
          user={user}
          additionalInfo={additionalInfo.filter(
            (key) => key !== "GUARANTOR_SKOR"
          )}
          formHandler={formHandler}
          updateUserProfile={updateUserProfile}
        />
      );
    if (cfg.id === "guarantor") {
      return (
        <TenantChecksGroupStep
          user={user}
          formData={formData}
          guarantor={guarantor}
          guarantorFormData={guarantorFormData}
          setGuarantorFormData={setGuarantorFormData}
          createUserProfile={createUserProfile}
          requiresSubcategory={requiresSubcategory}
          originalUser={originalUser}
          guarantorPhoneNumber={guarantorPhoneNumber}
          setGuarantorPhoneNumber={setGuarantorPhoneNumber}
          selectedProtectedHousingDocs={selectedProtectedHousingDocs}
          setSelectedProtectedHousingDocs={setSelectedProtectedHousingDocs}
          additionalInfo={additionalInfo}
          formHandler={formHandler}
          setStepId={setStepId}
          updateLLTenant={updateLLTenant}
          completeTenantCheck={completeTenantCheck}
          loadData={loadData}
          _t={_t}
        />
      );
    }
    return null;
  };

  return (
    <>
      {pageLoading ? (
        <div id="main-app-loader">
          <Loader />
        </div>
      ) : (
        <div className="tenant-onboarding-shell tenant-checks-shell">
          <TenantChecksSidebar visibleSteps={visibleSteps} activeId={stepId} />
          <div className="tenant-onboarding-main">
            {showPassedModal ? (
              <TenantChecksCompletedModal
                tenantName={user?.fname || formData?.fname || ""}
                _t={_t}
                onGoToDashboard={() => {
                  window.location.href = `/${workspaceId}/created-tenant`;
                }}
              />
            ) : (
              renderStep()
            )}
          </div>
        </div>
      )}
    </>
  );
}
export default TenantChecksOnboarding;
